import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import AppConfig from '../AppConfig';
import {
  collapseAtlasGlobalNavPanel,
  expandAtlasGlobalNavPanel,
} from '../components/GlobalNav/atlasGlobalNavPanel';
import { isContentAuthoringEditorPath } from '../components/GlobalNav/complianceEducationNavPaths';

const DEPLOYED_NAV_COLLAPSE_RETRY_MS = 100;
const DEPLOYED_NAV_COLLAPSE_MAX_ATTEMPTS = 30;

interface UseContentAuthoringEditorNavCollapseOptions {
  isLocalSideNavOpen: boolean;
  setIsLocalSideNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeployedNavReady: boolean;
}

/**
 * Collapses the app side nav when entering a content authoring editor route
 * (template and course editors). Restores prior state on leave.
 */
export function useContentAuthoringEditorNavCollapse({
  isLocalSideNavOpen,
  setIsLocalSideNavOpen,
  isDeployedNavReady,
}: UseContentAuthoringEditorNavCollapseOptions) {
  const { pathname } = useLocation();
  const isEditorRoute = isContentAuthoringEditorPath(pathname);
  const isLocalSideNavOpenRef = useRef(isLocalSideNavOpen);
  const storedLocalOpenRef = useRef<boolean | null>(null);
  const collapsedDeployedNavRef = useRef(false);

  isLocalSideNavOpenRef.current = isLocalSideNavOpen;

  useEffect(() => {
    if (!isEditorRoute) {
      if (AppConfig.isLocal && storedLocalOpenRef.current !== null) {
        setIsLocalSideNavOpen(storedLocalOpenRef.current);
        storedLocalOpenRef.current = null;
      }

      if (!AppConfig.isLocal && collapsedDeployedNavRef.current) {
        expandAtlasGlobalNavPanel();
        collapsedDeployedNavRef.current = false;
      }

      return;
    }

    if (AppConfig.isLocal) {
      if (storedLocalOpenRef.current === null) {
        storedLocalOpenRef.current = isLocalSideNavOpenRef.current;
        setIsLocalSideNavOpen(false);
      }
      return;
    }

    if (!isDeployedNavReady) {
      return;
    }

    let attempts = 0;
    const tryCollapse = () => {
      if (collapseAtlasGlobalNavPanel()) {
        collapsedDeployedNavRef.current = true;
        return true;
      }
      return false;
    };

    if (tryCollapse()) {
      return;
    }

    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (tryCollapse() || attempts >= DEPLOYED_NAV_COLLAPSE_MAX_ATTEMPTS) {
        window.clearInterval(intervalId);
      }
    }, DEPLOYED_NAV_COLLAPSE_RETRY_MS);

    return () => window.clearInterval(intervalId);
  }, [isEditorRoute, isDeployedNavReady, setIsLocalSideNavOpen]);
}
